/**
 * @file DatabaseTypes.js
 * WHAT: CRUD management interface for the database type reference table (e.g., ORACLE, MSSQL, MYSQL, POSTGRESQL, CASSANDRA).
 * WHY: Database types are a configuration prerequisite -- they populate dropdown options in UAR campaigns, app-database mappings, and connection management, so they must be maintained as a controlled vocabulary.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSafeTimeout } from '../hooks';

import API_CONFIG from '../config/api.config';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Renders a table of database type records with add/edit/delete modals and active/inactive status toggling.
 * WHY: Admins need to define which database technologies DAMA supports; these types drive campaign scoping, connection registration, and compliance reporting across the application.
 */
const DatabaseTypes = () => {
  const [databaseTypes, setDatabaseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentType, setCurrentType] = useState({
    type_name: '',
    type_description: '',
    is_active: 'Y'
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Safe timeout hook for auto-dismissing messages
  const { setSafeTimeout } = useSafeTimeout();

  useEffect(() => {
    fetchDatabaseTypes();
  }, []);

  const fetchDatabaseTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('config/database-types/all/'));
      if (response.data.success) {
        setDatabaseTypes(response.data.database_types);
      }
    } catch (error) {
      console.error('Error fetching database types:', error);
      setErrorMessage('Failed to load database types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setIsEdit(false);
    setCurrentType({
      type_name: '',
      type_description: '',
      is_active: 'Y'
    });
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (type) => {
    setIsEdit(true);
    // Convert boolean to 'Y'/'N' for the form
    setCurrentType({
      ...type,
      is_active: type.is_active === true || type.is_active === 'Y' ? 'Y' : 'N'
    });
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = (typeName) => {
    setDeleteTarget(typeName);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const response = await axios.delete(API_CONFIG.getDatabasesEndpoint(`config/database-types/${deleteTarget}/delete/`));
      if (response.data.success) {
        setSuccessMessage('Database type deleted successfully');
        setSafeTimeout(() => setSuccessMessage(''), 3000);
        fetchDatabaseTypes();
        setShowDeleteModal(false);
        setDeleteTarget(null);
      }
    } catch (error) {
      console.error('Error deleting database type:', error);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      // Show error in modal
      setErrorModalMessage(error.response?.data?.error || 'Failed to delete database type');
      setShowErrorModal(true);
    } finally {
      setDeleting(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!currentType.type_name.trim()) {
      newErrors.type_name = 'Type Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Convert 'Y'/'N' to boolean for backend
      const dataToSend = {
        ...currentType,
        is_active: currentType.is_active === 'Y'
      };

      let response;
      if (isEdit) {
        response = await axios.put(
          API_CONFIG.getDatabasesEndpoint(`config/database-types/${currentType.type_name}/update/`),
          {
            ...dataToSend,
            modified_by: 'ADMIN'
          }
        );
      } else {
        response = await axios.post(
          API_CONFIG.getDatabasesEndpoint('config/database-types/create/'),
          {
            ...dataToSend,
            created_by: 'ADMIN'
          }
        );
      }

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setSafeTimeout(() => setSuccessMessage(''), 3000);
        setShowModal(false);
        fetchDatabaseTypes();
      }
    } catch (error) {
      console.error('Error saving database type:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to save database type');
      setSafeTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentType(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{height: '100vh'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ backgroundColor: '#f8f9fc', minHeight: '100vh', padding: '30px' }}>
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h2 mb-1 fw-bold" style={{ color: '#2c3e50' }}>
            <i className="fas fa-database me-3" style={{ color: '#3498db' }}></i>
            Database Types
          </h1>
          <p className="text-muted mb-0">Manage database type options for UAR campaigns</p>
        </div>
        <div>
          <button
            className="btn btn-primary shadow-sm"
            onClick={handleAdd}
            style={{
              borderRadius: '10px',
              padding: '10px 24px',
              fontWeight: '600'
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Add Database Type
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
        </div>
      )}

      {/* Database Types Table */}
      <div className="card shadow-lg border-0 mb-4" style={{ borderRadius: '15px', overflow: 'hidden' }}>
        <div className="card-header py-4" style={{
          background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
          borderBottom: '2px solid #e2e8f0'
        }}>
          <h5 className="m-0 fw-bold" style={{ color: '#2d3748' }}>
            <i className="fas fa-list me-2"></i>
            All Database Types
          </h5>
        </div>
        <div className="card-body" style={{ padding: '0' }}>
          {databaseTypes.length === 0 ? (
            <div className="alert alert-info text-center m-4" style={{ borderRadius: '10px' }}>
              <i className="fas fa-info-circle me-2"></i>
              No database types found. Add your first database type to get started!
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th className="border-0 py-3 px-4" style={{ fontWeight: '700', color: '#2c3e50' }}>Type Name</th>
                    <th className="border-0 py-3" style={{ fontWeight: '700', color: '#2c3e50' }}>Description</th>
                    <th className="border-0 py-3" style={{ fontWeight: '700', color: '#2c3e50' }}>Status</th>
                    <th className="border-0 py-3" style={{ fontWeight: '700', color: '#2c3e50' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {databaseTypes.map((type, index) => (
                    <tr key={type.type_id} style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fc',
                      transition: 'all 0.3s ease'
                    }}>
                      <td className="py-3 px-4">
                        <strong style={{ color: '#2c3e50', fontSize: '1.05rem' }}>{type.type_name}</strong>
                      </td>
                      <td className="py-3">
                        <span className="text-muted">{type.type_description || 'N/A'}</span>
                      </td>
                      <td className="py-3">
                        {type.is_active === true || type.is_active === 'Y' ? (
                          <span className="badge" style={{
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#fff',
                            background: '#48bb78'
                          }}>
                            <i className="fas fa-check-circle me-1"></i>Active
                          </span>
                        ) : (
                          <span className="badge" style={{
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#fff',
                            background: '#718096'
                          }}>
                            <i className="fas fa-times-circle me-1"></i>Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(type)}
                          style={{ borderRadius: '8px' }}
                        >
                          <i className="fas fa-edit me-1"></i>Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(type.type_name)}
                          style={{ borderRadius: '8px' }}
                        >
                          <i className="fas fa-trash me-1"></i>Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ borderRadius: '15px' }}>
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderTopLeftRadius: '15px',
                borderTopRightRadius: '15px'
              }}>
                <h5 className="modal-title fw-bold">
                  <i className={`fas ${isEdit ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                  {isEdit ? 'Edit Database Type' : 'Add New Database Type'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Type Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.type_name ? 'is-invalid' : ''}`}
                      name="type_name"
                      value={currentType.type_name}
                      onChange={handleInputChange}
                      disabled={isEdit}
                      placeholder="e.g., ORACLE, POSTGRESQL, MYSQL"
                      style={{ borderRadius: '10px' }}
                    />
                    {errors.type_name && (
                      <div className="invalid-feedback">{errors.type_name}</div>
                    )}
                    <div className="form-text">
                      <i className="fas fa-info-circle me-1"></i>
                      Type Name is used as a unique identifier. Cannot be changed after creation.
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Description</label>
                    <textarea
                      className="form-control"
                      name="type_description"
                      value={currentType.type_description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Enter database type description"
                      style={{ borderRadius: '10px' }}
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Status</label>
                    <select
                      className="form-select"
                      name="is_active"
                      value={currentType.is_active}
                      onChange={handleInputChange}
                      style={{ borderRadius: '10px' }}
                    >
                      <option value="Y">Active</option>
                      <option value="N">Inactive</option>
                    </select>
                  </div>
                </form>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e2e8f0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-times me-2"></i>Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-save me-2"></i>
                  {isEdit ? 'Update Type' : 'Create Type'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1050,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '600px', width: '100%', margin: '50px auto', borderRadius: '15px' }}>
              <div className="modal-header bg-danger text-white" style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Delete Database Type
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  disabled={deleting}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-danger mb-4">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  <strong>Warning:</strong> This action cannot be undone. The database type will be permanently removed from the system.
                </div>

                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <strong>Database Type to Delete</strong>
                  </div>
                  <div className="card-body">
                    <table className="table table-sm mb-0">
                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ width: '150px' }}>Type Name:</td>
                          <td><span className="badge bg-secondary fs-6">{deleteTarget}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="alert alert-warning mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Note:</strong> Database types that are currently in use by database mappings cannot be deleted. You must remove all associated mappings first.
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e2e8f0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  disabled={deleting}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-times me-2"></i>Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={deleting}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>Delete Database Type
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '15px' }}>
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
                color: 'white',
                borderTopLeftRadius: '15px',
                borderTopRightRadius: '15px'
              }}>
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  Cannot Delete
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowErrorModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="text-center">
                  <i className="fas fa-ban fa-3x text-warning mb-3"></i>
                  <p className="mb-0">{errorModalMessage}</p>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e2e8f0' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowErrorModal(false)}
                  style={{ borderRadius: '10px', padding: '8px 20px' }}
                >
                  <i className="fas fa-check me-2"></i>OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseTypes;
